/**
 * @module MediaCard
 */
import React from 'react';
import {
  fetchMedia,
} from '../../helpers/dataFetchers/findsFetchers';
import CardWithTags from '../CardWithTags/CardWithTags';

/**
 * Represents a component that fetches and displays an media card with tags.
 *
 * @param {object} props - The component props object.
 * @param {object} [props.fetchedMedia] - Optional prefetched media data.
 * @param {boolean} [props.isFullImage] - The boolean flag indicating whether to display the image in full width.
 * @param {string} [props.media] - The media uuid of which to fetch data.
 * @param {object} [props.preload] - Optional preloaded media data.
 * @param {string} props.theme - The theme value for styling the card.
 *
 * @returns {React.ReactElement} - The MediaCard component.
 */
function MediaCard({
  fetchedMedia,
  isFullImage = false,
  media,
  preload,
  theme,
}) {
  const [data, setData] = React.useState(null);

  /**
   * Single-run convenience effect to fetch and set the data for the component.
   */
  React.useEffect(() => {
    async function fetchData() {
      if (!(fetchedMedia || preload)) {
        setData(await fetchMedia({ media }));
      } else {
        setData(fetchedMedia || preload);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  const isMediaCollection = () => {
    return data?.['@nodeType']?.startsWith('lc:collection');
  };

  const getImage = () => {
    if (isMediaCollection()) {
      return data?.channelImageList?.cinemaImage_public;
    }
    return data?.featuredImage_public;
  };



  return data ? (
    <CardWithTags
      author={data.author}
      description={data.description}
      hasFullWidthImage={isFullImage}
      image={getImage()}
      isMedia={true}
      link={data.shareableUrl ?? 'https://www.life.church/media/'}
      theme={theme}
      title={data.title}
    />
  ) : null;
}

export default MediaCard;
